import React, { JSX, RefObject, useEffect, useRef, useState } from 'react';
import { useMount } from 'react-use';
import { Alert, Button, Col, Row } from 'reactstrap';
import dayjs from 'dayjs';
import i18next from 'i18next';
import minMax from 'dayjs/plugin/minMax';
import {
  ApiHotel,
  ApiListOptions,
  ApiPackageCart,
  ApiPackageModel,
  ApiRoomContainer
} from '@ibe/api';
import {
  DatePickerRef,
  HotelStore,
  LoadingOverlay,
  ServiceSearchDetailParams,
  ServiceSearchParams,
  useConfig
} from '@ibe/components';
import Keys from '../../Translations/generated/service-search.en.json.keys';
import ServiceSearchProps from './ServiceSearchProps';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faCheckSquare,
  faEdit,
  faUser,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { useOnClickOutside } from '../../Util/useOnClickOutside';
import MoveTicketNotification from '../MoveTicketNotification/MoveTicketNotification';
import CmsOffer from '../../Models/CMS/CmsOffer';
import utc from 'dayjs/plugin/utc';
import isBetween from 'dayjs/plugin/isBetween';
import DateRangeInput from '../Datepicker/DateRangeInput/DateRangeInput';
import MegConfigModel from '../../Config/MegConfigModel';
import useCmsPackage from '../../Hooks/useCmsPackage';
import CmsPackage from '../../Models/CMS/CmsPackage';
import { QueryUtils } from '../../Util/QueryUtils';
import useQuery from '../../Util/useQuery';
import DealFinder from '../DealFinder/DealFinder';
import DealFinderPropsResolver from '../DealFinder/DealFinderPropsResolver';
import { useTranslation } from 'react-i18next';
import {
  ActivePackageConfiguration,
  DealFinderWidgetModeProps
} from '../DealFinder/DealFinderProps';
import useIsLoading from '../../Hooks/useIsLoading';
import { DateRangePickerRef } from '../Datepicker/DatePicker/DatePicker';
import { dealFinderOpenDatePickerEvent } from '../DealFinder/DealFinderEvents';
import PaxRoomSelection from '../PaxRoomSelection/PaxRoomSelection';

dayjs.extend(minMax);
dayjs.extend(utc);
dayjs.extend(isBetween);

const INVALID_DATE_STRING = 'Invalid Date';

export const MegServiceSearch = (
  props: Omit<ServiceSearchProps, 'datePickerRef'> & {
    containerRef?: RefObject<HTMLDivElement | null>;
    datePickerRef?: RefObject<DatePickerRef>;
    dateRangeInputRef?: RefObject<DateRangePickerRef | null>;

    editDateRef?: RefObject<HTMLDivElement>;
    accommodationBar?: JSX.Element;
    hotelModel?: ApiHotel;
    withRoomCountSelector?: boolean;
    minDateRangeSpan?: number;
    widgetView?: boolean;
    withDealFinderWidget?: boolean;
    position?: 'TOP' | 'BOTTOM';
    buyTicketsUrl?: string;
    store?: HotelStore;
    offers?: CmsOffer[];
    packageModel?: CmsPackage;
    packageCart?: ApiPackageCart;
    onDealFinderHotelSearch?: (
      params: Partial<ServiceSearchDetailParams>,
      listOptions: ApiListOptions
    ) => void;
    dealFinderWidgetModeProps?: DealFinderWidgetModeProps;
    onDealFinderExpand?: () => void;
  }
): JSX.Element => {
  const {
    containerRef,
    searchParams,
    maxDateRangeSpan,
    maxRooms,
    onSubmit,
    maxAdults,
    seasons,
    maxChildren,
    maxChildAge,
    datePickerRef,
    moveTicketMessage,
    accommodationBar,
    hotelModel,
    withRoomCountSelector,
    minDateRangeSpan,
    editDateRef,
    widgetView,
    position,
    buyTicketsUrl,
    store,
    packageCart,
    offers: offersFromProps,
    packageModel: packageModelFromProps,
    onDealFinderHotelSearch,
    dealFinderWidgetModeProps,
    withDealFinderWidget,
    onDealFinderExpand,
    dateRangeInputRef
  } = props;

  const { t } = useTranslation('service-search');
  useTranslation('plus-minus');
  const config = useConfig() as MegConfigModel;
  const cmsPackageService = useCmsPackage();
  const query = useQuery();

  const propsResolver = new DealFinderPropsResolver(widgetView, query, searchParams);

  const packageCodeFromQuery = QueryUtils.getPackageCode(query);
  const salesChannelCodeFromQuery = QueryUtils.getSalesChannelCode(query);
  const occupancyFromQuery = QueryUtils.getOccupancy(query);
  const startDateFromQuery = QueryUtils.getSearchStartDate(query);
  const endDateFromQuery = QueryUtils.getSearchEndDate(query);
  const itemFromQuery = QueryUtils.getItem(query);

  const format =
    config.displayFormatDate[i18next.language || localStorage.getItem('i18nextLng') || ''] ||
    config.displayFormatDate[config.defaultLanguage];
  const imperativeHandleRef = useRef<{
    getTitle: () => string;
    toggleDropdown: () => void;
    getCurrentPax: () => ApiRoomContainer[];
    validateChildrenSelection: () => boolean;
  }>(null);
  const { isLoading, setIsLoading } = useIsLoading();
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [dealFinderOpen, setDealFinderOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false);
  const [datePickerOpenConfirm, setDatePickerOpenConfirm] = useState<boolean>(false);
  const [paxSelection, setPaxSelection] = useState<ApiRoomContainer[]>(
    searchParams?.occupancy || []
  );
  const [dateRange, setDateRange] = useState<[string | undefined, string | undefined]>([
    undefined,
    undefined
  ]);
  const [paxDropdownOpen, setPaxDropdownOpen] = useState<boolean>(false);
  const [packageModel, setPackageModel] = useState<CmsPackage | undefined>();
  const [alternativePackageModel, setAlternativePackageModel] = useState<CmsPackage | undefined>();
  const [activePackageConfiguration, setActivePackageConfiguration] = useState<
    ActivePackageConfiguration | undefined
  >({
    activePackageModel: packageModel,
    activePackageSalesChannelCode: salesChannelCodeFromQuery ?? ''
  });

  const paxDropdownOpenRef = useRef<boolean>(false);
  const dateEditRef = useRef<HTMLElement | null>(null);

  const occupancyFromProps = searchParams?.occupancy;
  const startDateFromProps = searchParams?.startDate;
  const endDateFromProps = searchParams?.endDate;

  const isSingleDateSelection = minDateRangeSpan == 0;

  const useDealFinder = propsResolver.getResolvedUseDealFinder(
    !!withDealFinderWidget,
    packageModel
  );

  const shouldRenderDealFinder = useDealFinder && (packageModel || widgetView);

  useOnClickOutside([dateEditRef, popoverRef], (): void => {
    if (!datePickerOpen) {
      setDatePickerOpenConfirm(false);
    }
  });

  useMount(() => {
    setIsLoading(true);

    if (!packageModelFromProps || withDealFinderWidget) {
      loadPackageModel();
    } else {
      setPackageModel(packageModelFromProps);
      setActivePackageConfiguration({
        activePackageModel: packageModelFromProps,
        activePackageSalesChannelCode: salesChannelCodeFromQuery ?? ''
      });
    }

    if (widgetView) {
      setDealFinderOpen(!!dealFinderWidgetModeProps?.expandDealFinder);
    } else if (!occupancyFromQuery || !startDateFromQuery || !endDateFromQuery) {
      setDealFinderOpen(true);
    }
    setIsLoading(false);

    dealFinderOpenDatePickerEvent.subscribe(() => setDealFinderOpen(true));
  });

  useEffect(() => {
    if (packageModel || withDealFinderWidget) {
      const alternativePackageCode =
        dealFinderWidgetModeProps?.alternativePackageCode ??
        packageModel?.dealFinderConfig?.alternativePackageCode;

      if (alternativePackageCode) {
        setIsLoading(true);
        cmsPackageService
          .fetchPackage(alternativePackageCode as string) //NOSONAR
          .then(returnedPackageModel => {
            if (returnedPackageModel) {
              setAlternativePackageModel(returnedPackageModel as CmsPackage); //NOSONAR
            }
          })
          .finally(() => setIsLoading(false));
      }
    }
  }, [packageModel?.name]);

  useEffect(() => {
    if (occupancyFromQuery && startDateFromQuery && endDateFromQuery) {
      setDealFinderOpen(false);
    }
  }, [occupancyFromQuery, startDateFromQuery, endDateFromQuery]);

  useEffect(() => {
    if (occupancyFromProps) {
      setPaxSelection([...occupancyFromProps]);
    }
    let newDateRange: [string | undefined, string | undefined] = [undefined, undefined];
    if (!!startDateFromProps && startDateFromProps !== INVALID_DATE_STRING) {
      newDateRange = [startDateFromProps, newDateRange[1]];
    }
    if (!!endDateFromProps && endDateFromProps !== INVALID_DATE_STRING) {
      newDateRange = [newDateRange[0], endDateFromProps];
    }
    setDateRange(newDateRange);
  }, [occupancyFromProps, startDateFromProps, endDateFromProps]);

  useEffect(() => {
    setTimeout(() => {
      if (!paxDropdownOpen) {
        paxDropdownOpenRef.current = false;
      }
    }, 0);
  }, [paxDropdownOpen]);

  useEffect(() => {
    setAlternativePackageModel(undefined);
    loadPackageModel();
  }, [packageCodeFromQuery]);

  function loadPackageModel() {
    let packageCode;

    if (withDealFinderWidget) {
      packageCode = searchParams?.code;
    } else {
      packageCode = QueryUtils.getPackageCode(query);
    }
    if (packageCode) {
      setIsLoading(true);
      cmsPackageService
        .fetchPackage(packageCode)
        .then(returnedPackageModel => {
          if (returnedPackageModel) {
            setPackageModel(returnedPackageModel as CmsPackage); //NOSONAR
            setActivePackageConfiguration({
              activePackageModel: returnedPackageModel,
              activePackageSalesChannelCode: salesChannelCodeFromQuery ?? ''
            }); //NOSONAR
          }
        })
        .finally(() => setIsLoading(false));
    }
  }

  const hasSelectedPax = (): boolean => {
    return paxSelection.some(
      selection => !!selection.adults || !!selection.children || !!selection.infants
    );
  };

  const getNumberOfAdults = (): number => {
    return (
      paxSelection?.reduce((total: number, current: ApiRoomContainer) => {
        return total + (current.adults || 0);
      }, 0) || 0
    );
  };

  const getNumberOfChildren = (): number => {
    return (
      paxSelection?.reduce((total: number, current: ApiRoomContainer) => {
        return total + (current.children || 0) + (current.infants || 0);
      }, 0) || 0
    );
  };

  const hasDateRange = (): boolean => {
    return (
      !!dateRange &&
      !!dateRange[0] &&
      dateRange[0] !== INVALID_DATE_STRING &&
      !!dateRange[1] &&
      dateRange[1] !== INVALID_DATE_STRING
    );
  };

  const isDateRangeValid = (
    startDate: string | undefined,
    endDate: string | undefined
  ): boolean => {
    if (
      !startDate ||
      !endDate ||
      (!!startDate && startDate === INVALID_DATE_STRING) ||
      (!!endDate && endDate === INVALID_DATE_STRING)
    )
      return false;

    const dayjsStart = dayjs(startDate, config.formatDate);
    const dayjsEnd = dayjs(endDate, config.formatDate);

    if (!dayjsStart.isValid() || !dayjsEnd.isValid()) {
      return false;
    }
    if (dayjsStart.isAfter(dayjsEnd)) {
      return false;
    }
    if (dayjsStart.isSame(dayjsEnd) && minDateRangeSpan !== 0) {
      return false;
    }
    const now = dayjs().startOf('day');
    return dayjsStart.isSame(now) || dayjsStart.isAfter(now);
  };

  const handleSubmit = (
    values: Partial<ServiceSearchParams>,
    overridingParameters?: Partial<ServiceSearchParams>
  ): void => {
    if (onSubmit) {
      onSubmit(
        {
          region: values.region,
          startDate: values.startDate,
          endDate: values.endDate,
          occupancy: values.occupancy || [],
          metaData: values.metaData!,
          unitTypeCode: values.unitTypeCode!
        },
        overridingParameters
      );
    }
  };

  const minDay = new Date();
  minDay.setDate(minDay.getDate() + config.datePickerMinDateOffset);

  const toggleOverlay = () => {
    if (paxDropdownOpenRef.current) {
      paxDropdownOpenRef.current = false;
      const newPax = imperativeHandleRef?.current?.getCurrentPax();
      if (!!newPax && imperativeHandleRef?.current?.validateChildrenSelection()) {
        setPaxSelection(newPax);
        if (
          hasDateRange() &&
          isDateRangeValid(dateRange[0], dateRange[1]) &&
          !!searchParams &&
          !widgetView
        ) {
          handleSubmit({
            ...searchParams,
            occupancy: newPax,
            startDate: dateRange[0],
            endDate: dateRange[1]
          });
        }
      }
    } else {
      imperativeHandleRef?.current?.toggleDropdown();
      paxDropdownOpenRef.current = true;
    }
  };

  const handlePaxDropdownOpenChange = (dropdownOpen: boolean): void => {
    setPaxDropdownOpen(dropdownOpen);
  };

  function toggleDealFinder() {
    setDealFinderOpen(!dealFinderOpen);

    if (onDealFinderExpand) {
      onDealFinderExpand();
    }
  }

  const toggleDatePicker = (): void => {
    let newDateRange: [startDate?: string, endDate?: string] | undefined = undefined;
    if (dateRangeInputRef?.current) {
      if (datePickerOpen) {
        dateRangeInputRef.current.closePopover();
        setDatePickerOpen(false);
      } else {
        if (datePickerOpenConfirm) {
          setDatePickerOpenConfirm(false);
          newDateRange = dateRangeInputRef.current.getCurrentDateRange();
        }
        if (!datePickerOpenConfirm) {
          dateRangeInputRef.current.openPopover();
          setDatePickerOpen(true);
          setDatePickerOpenConfirm(true);
        }
      }
    }
    if (newDateRange) {
      handleDateSubmit(newDateRange[0], newDateRange[1], true);
    }
  };

  const handleCalendarClose = (): void => {
    setDatePickerOpen(false);
  };

  const updateDateValues = (
    startDate?: string,
    endDate?: string,
    ignoreClose?: boolean
  ): [string | undefined, string | undefined] => {
    let value = [...dateRange] as [string | undefined, string | undefined];
    if (startDate) {
      value = [dayjs(startDate).format(config.formatDate), value[1]];
    }
    if (endDate) {
      value = [value[0], dayjs(endDate).format(config.formatDate)];
    }
    setDateRange(value);
    if (!!dateRangeInputRef?.current && !ignoreClose) {
      dateRangeInputRef.current.closePopover();
      setDatePickerOpen(false);
      setDatePickerOpenConfirm(false);
    }
    return [value[0], value[1]];
  };

  const handleWidgetCalendarClose = (startDate?: string, endDate?: string): void => {
    updateDateValues(startDate, endDate);
    setDatePickerOpen(false);
  };

  const handleDateSubmit = (startDate?: string, endDate?: string, ignoreClose?: boolean): void => {
    const [start, end] = updateDateValues(startDate, endDate, ignoreClose);
    if (hasSelectedPax() && isDateRangeValid(start, end) && !!searchParams) {
      handleSubmit({
        ...searchParams,
        occupancy: [...paxSelection],
        startDate: start,
        endDate: end
      });
    }
  };

  const handlePaxChange = (pax: ApiRoomContainer[]): void => {
    setPaxSelection([...pax]);
    if (
      pax &&
      hasDateRange() &&
      isDateRangeValid(dateRange[0], dateRange[1]) &&
      !!searchParams &&
      !widgetView
    ) {
      handleSubmit({
        ...searchParams,
        occupancy: [...pax],
        startDate: dateRange[0],
        endDate: dateRange[1]
      });
    }
  };

  const handleTicketUrlRedirection = () => {
    if (buyTicketsUrl) {
      window.location.href = buyTicketsUrl;
    }
  };

  function handleAlternativePackageSelection(
    newActivePackageModel: ApiPackageModel,
    newActiveSalesChannelCode: string
  ) {
    setActivePackageConfiguration({
      activePackageModel: newActivePackageModel,
      activePackageSalesChannelCode: newActiveSalesChannelCode
    });
  }

  function renderAlternativePackageOptions(): JSX.Element {
    if (useDealFinder && packageModel && alternativePackageModel && !itemFromQuery) {
      return (
        <Row className="justify-content-start align-items-end meg_deal-finder_alternative_packages_container">
          <Col className="col-auto px-1">
            <button
              className={`meg_deal-finder_alternative_package ${
                packageModel?.name == activePackageConfiguration?.activePackageModel?.name &&
                'meg_deal-finder_alternative_package__selected'
              }`}
              onClick={() => {
                handleAlternativePackageSelection(
                  packageModel as ApiPackageModel,
                  salesChannelCodeFromQuery ?? ''
                );
              }}
            >
              {packageModel.title}
            </button>
          </Col>
          <Col className="col-auto px-1">
            <button
              className={`meg_deal-finder_alternative_package ${
                alternativePackageModel?.name ==
                  activePackageConfiguration?.activePackageModel?.name &&
                'meg_deal-finder_alternative_package__selected'
              }`}
              onClick={() => {
                handleAlternativePackageSelection(
                  alternativePackageModel as ApiPackageModel,
                  packageModel?.dealFinderConfig?.alternativePackageSalesChannelCode ?? ''
                );
              }}
            >
              {alternativePackageModel.title}
            </button>
          </Col>
        </Row>
      );
    }

    return <></>;
  }

  function renderDealFinder(): JSX.Element {
    if (shouldRenderDealFinder) {
      return (
        <DealFinder
          isInWidgetMode={widgetView}
          isEnabled={dealFinderOpen}
          packageModel={packageModel}
          isMobileView={false}
          searchParams={searchParams}
          onSubmit={handleSubmit}
          onHotelSearch={onDealFinderHotelSearch}
          packageCart={packageCart}
          datePickerProps={{
            minDateRangeSpan,
            maxDateRangeSpan,
            seasons,
            widgetView
          }}
          store={store}
          activePackageConfigurationFromProps={activePackageConfiguration}
          dealFinderWidgetModeProps={dealFinderWidgetModeProps}
          minAdults={1}
          maxRooms={maxRooms}
          maxCapacity={100}
          maxAdults={maxAdults}
          maxChildren={maxChildren}
          maxChildAge={maxChildAge}
        />
      );
    } else {
      return <></>;
    }
  }

  function paxLabel(): JSX.Element {
    const noOfAdults =
      getNumberOfAdults() > 0
        ? `, ${getNumberOfAdults()} ${t(Keys.adult, {
            count: getNumberOfAdults()
          })}`
        : '';

    const noOfChildren =
      getNumberOfChildren() > 0
        ? `, ${getNumberOfChildren()} ${t(Keys.child, {
            count: getNumberOfChildren()
          })}`
        : '';

    return (
      <>
        {hasSelectedPax()
          ? `${paxSelection.length} ${t(Keys.room, {
              count: paxSelection.length
            })}${noOfAdults}${noOfChildren}`
          : t(Keys.pleaseSelect)}
      </>
    );
  }
  function dateLabel(): JSX.Element {
    const dateRangeSpan =
      minDateRangeSpan === 0
        ? `${dayjs(dateRange[0]).format(format)}`
        : `${dayjs(dateRange[0]).format(format)} - ${dayjs(dateRange[1]).format(format)}`;
    return <>{hasDateRange() ? dateRangeSpan : t(Keys.pleaseSelect)}</>;
  }

  function submitRenderer(): JSX.Element {
    return widgetView ? (
      <Row className="iso__service-search__submit">
        <Col xs={buyTicketsUrl ? 6 : 12}>
          <Button
            color="primary"
            data-testid="serviceSearch_button_search"
            type="submit"
            className="iso__service-search__button w-100"
            onClick={() => handleDateSubmit()}
          >
            {t(Keys.submit)}
          </Button>
        </Col>
        {!!buyTicketsUrl && (
          <Col xs={6}>
            <Button
              color="outline-primary"
              data-testid="serviceSearch_button_tickets"
              type="submit"
              className="iso__service-search__button w-100"
              onClick={handleTicketUrlRedirection}
            >
              {t(Keys.ticketsButtonLabel)}
            </Button>
          </Col>
        )}
      </Row>
    ) : (
      <> </>
    );
  }

  function moveTicketMessageRenderer(): JSX.Element {
    return moveTicketMessage ? (
      <Col sm={12}>
        <MoveTicketNotification message={moveTicketMessage} />
      </Col>
    ) : (
      <> </>
    );
  }

  const onDateEdit = useDealFinder ? toggleDealFinder : toggleDatePicker;

  const customTitle: React.ReactNode = (
    <span
      className={`iso__service-search__meg__edit__toggle${
        paxDropdownOpen ? ' iso__service-search__meg__edit__toggle--toggled' : ''
      }`}
      onClick={event => {
        if (useDealFinder) {
          event.stopPropagation();
          event.preventDefault();
          toggleDealFinder();
        }
      }}
    >
      <span>{paxDropdownOpen ? t(Keys.confirm) : t(Keys.edit)}</span>
      <span>
        <FontAwesomeIcon icon={paxDropdownOpen ? faCheckSquare : faEdit} />
      </span>
    </span>
  );

  const serviceSearchClassName = (): string => {
    return `iso__service-search iso__service-search__meg justify-content-lg-start justify-content-center ${
      widgetView && 'withButton'
    } ${position === 'BOTTOM' && 'upwards'}`;
  };

  return (
    <LoadingOverlay isLoading={isLoading}>
      <>
        <div ref={containerRef}>
          <div className={useDealFinder ? 'iso__service-search__meg-with-deal_finder' : ''}>
            <div>
              {renderAlternativePackageOptions()}
              <Row className={serviceSearchClassName()}>
                <Col
                  xs={12}
                  lg={6}
                  className="pb-1 iso__service-search__pax-section"
                  onClick={event => {
                    if (useDealFinder) {
                      event.stopPropagation();
                      event.preventDefault();
                      toggleDealFinder();
                    }
                  }}
                >
                  <div
                    className="iso__service-search__meg__edit"
                    onClick={useDealFinder ? toggleDealFinder : toggleOverlay}
                  >
                    <span>
                      <FontAwesomeIcon icon={faUser} className="mr-2" />
                      {paxLabel()}
                    </span>
                  </div>
                  <PaxRoomSelection
                    childAgePlaceholder={t(Keys.select)}
                    initialOccupancy={[...paxSelection]}
                    value={[...paxSelection]}
                    onChange={handlePaxChange}
                    withRoomCountSelector={withRoomCountSelector}
                    maxRooms={maxRooms}
                    minAdults={1}
                    maxAdults={maxAdults}
                    maxChildren={maxChildren}
                    maxChildAge={maxChildAge}
                    maxCapacity={100}
                    imperativeHandleRef={imperativeHandleRef}
                    rightAlignedMenu
                    onDropdownOpenChange={(dropdownOpen: boolean) =>
                      useDealFinder ? toggleDealFinder : handlePaxDropdownOpenChange(dropdownOpen)
                    }
                    fillWithValueOnOpen
                    useInitialPersonAmounts
                    customTitle={customTitle}
                    skipSearch={widgetView}
                    tabIndex={-1}
                    accessibilityModal
                    useCustomDropdownToggle={!widgetView}
                  />
                </Col>
                <Col xs={12} lg={6} className="pb-1 iso__service-search__date-section">
                  <div
                    ref={editDateRef}
                    className="iso__service-search__meg__edit"
                    onClick={onDateEdit}
                  >
                    <span>
                      <FontAwesomeIcon icon={faCalendar} className="mr-2" />
                      {dateLabel()}
                    </span>
                    <button onClick={onDateEdit} className="border-0 p-0 m-0 bg-transparent">
                      <span
                        ref={dateEditRef as RefObject<HTMLSpanElement>}
                        className={`iso__service-search__meg__edit__toggle${
                          datePickerOpen ? ' iso__service-search__meg__edit__toggle--toggled' : ''
                        }`}
                      >
                        <span>{datePickerOpen ? t(Keys.confirm) : t(Keys.edit)}</span>
                        <span>
                          <FontAwesomeIcon icon={datePickerOpen ? faCheckSquare : faEdit} />
                        </span>
                      </span>
                    </button>
                  </div>
                  {datePickerOpen && (
                    <div
                      className="dateRangeInput__mobile-backdrop"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (dateRangeInputRef?.current) {
                          dateRangeInputRef.current.closePopover();
                        }
                        setDatePickerOpen(false);
                        setDatePickerOpenConfirm(false);
                      }}
                      onTouchStart={e => e.stopPropagation()}
                    />
                  )}
                  <DateRangeInput
                    dataTestId="serviceSearch_selectDate"
                    startDate={dateRange[0]}
                    endDate={dateRange[1]}
                    minDate={minDay}
                    minDateRangeSpan={minDateRangeSpan ?? 1}
                    maxDateRangeSpan={maxDateRangeSpan}
                    seasons={seasons}
                    datePickerRef={datePickerRef}
                    monthsShown={1}
                    selectsDisabledDaysInRange
                    customDivider={<span>-</span>}
                    onCalendarClose={widgetView ? undefined : handleCalendarClose}
                    onCalendarCloseDated={widgetView ? handleWidgetCalendarClose : undefined}
                    popoverRef={popoverRef}
                    popoverTargetRef={dateEditRef as RefObject<HTMLElement>}
                    dateRangeInputRef={dateRangeInputRef}
                    onSubmit={widgetView ? undefined : handleDateSubmit}
                    CustomHeader={
                      <div className="iso__service-search__meg__date-range__custom">
                        <span>{t(Keys.hotelStayDates)}</span>
                        <button
                          className="dateRangeInput__mobile-close"
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (dateRangeInputRef?.current) {
                              dateRangeInputRef.current.closePopover();
                            }
                            setDatePickerOpen(false);
                            setDatePickerOpenConfirm(false);
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    }
                    stateFromPropsOnPopoverOpen
                    useLocalSelectedDatesForInRangeCheck
                    onChange={(startDate: string | undefined, endDate: string | undefined) => {
                      if (startDate && endDate && isSingleDateSelection && !widgetView) {
                        handleDateSubmit(startDate, endDate);
                      }
                    }}
                    withColorCodedAvailability={
                      hotelModel?.metaInfo?.contentByMetaType?.AVAILABILITY_CALENDAR?.content
                        ?.isAvailabilityCalendarEnabled as boolean | undefined
                    }
                    searchParams={searchParams}
                    selectsRange={!isSingleDateSelection}
                  />
                </Col>
                {submitRenderer()}
                {moveTicketMessageRenderer()}
              </Row>
            </div>
          </div>
        </div>
        {shouldRenderDealFinder && (
          <Row>
            <Col className={'d-flex justify-content-center'}>{renderDealFinder()}</Col>
          </Row>
        )}
        {hasDateRange() && !isDateRangeValid(dateRange[0], dateRange[1]) && (
          <Alert className="mt-3" color="danger">
            {t(Keys.invalidInput)}
          </Alert>
        )}
        {accommodationBar}
      </>
    </LoadingOverlay>
  );
};
